'use client';

import { useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface TrustBadge {
  icon: string;
  title: string;
  description: string;
}

interface TrustBadgeCarouselProps {
  className?: string;
}

const TrustBadgeCarousel = ({ className = '' }: TrustBadgeCarouselProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const trustBadges: TrustBadge[] = [
    {
      icon: 'ShieldCheckIcon',
      title: 'Secure Trading',
      description: 'Bank-grade security for all transactions'
    },
    {
      icon: 'BuildingOfficeIcon',
      title: 'Local Office',
      description: 'Visit us in Hyderabad anytime'
    },
    {
      icon: 'UsersIcon',
      title: '2000+ Customers',
      description: 'Trusted by local traders'
    },
    {
      icon: 'ClockIcon',
      title: 'Instant Response',
      description: 'Average reply time: 2 minutes'
    },
    {
      icon: 'DocumentCheckIcon',
      title: 'Fully Compliant',
      description: 'Following all Indian regulations'
    },
    {
      icon: 'CurrencyRupeeIcon',
      title: 'Cash Accepted',
      description: 'Convenient cash transactions'
    }
  ];

  const checkScrollButtons = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = 300;
      const newScrollLeft = direction === 'left'
        ? scrollContainerRef.current.scrollLeft - scrollAmount
        : scrollContainerRef.current.scrollLeft + scrollAmount;
      
      scrollContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    checkScrollButtons();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollButtons);
      return () => container.removeEventListener('scroll', checkScrollButtons);
    }
  }, []);

  return (
    <div data-component-id="src/components/common/TrustBadgeCarousel.tsx:86:4" data-component-path="src/components/common/TrustBadgeCarousel.tsx" data-component-line="86" data-component-end-line="136" data-component-file="TrustBadgeCarousel.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`relative ${className}`}>
      {/* Scroll Left Button */}
      {canScrollLeft && (
        <button data-component-id="src/components/common/TrustBadgeCarousel.tsx:89:8" data-component-path="src/components/common/TrustBadgeCarousel.tsx" data-component-line="89" data-component-end-line="95" data-component-file="TrustBadgeCarousel.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22absolute%20left-0%20top-1%2F2%20-translate-y-1%2F2%20z-10%20bg-card%20shadow-elevated%20rounded-full%20p-2%20hover%3Abg-muted%20transition-smooth%22%7D"
          onClick={() => scroll('left')}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-card shadow-elevated rounded-full p-2 hover:bg-muted transition-smooth"
          aria-label="Scroll left"
        >
          <Icon data-component-id="src/components/common/TrustBadgeCarousel.tsx:94:10" data-component-path="src/components/common/TrustBadgeCarousel.tsx" data-component-line="94" data-component-end-line="94" data-component-file="TrustBadgeCarousel.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ChevronLeftIcon%22%7D" name="ChevronLeftIcon" size={20} variant="solid" />
        </button>
      )}

      {/* Badges Container */}
      <div data-component-id="src/components/common/TrustBadgeCarousel.tsx:99:6" data-component-path="src/components/common/TrustBadgeCarousel.tsx" data-component-line="99" data-component-end-line="124" data-component-file="TrustBadgeCarousel.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20overflow-x-auto%20scrollbar-hide%20space-x-4%20px-2%20py-4%20snap-x%20snap-mandatory%22%7D"
        ref={scrollContainerRef}
        className="flex overflow-x-auto scrollbar-hide space-x-4 px-2 py-4 snap-x snap-mandatory"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {trustBadges.map((badge, index) => (
          <div data-component-id="src/components/common/TrustBadgeCarousel.tsx:105:10" data-component-path="src/components/common/TrustBadgeCarousel.tsx" data-component-line="105" data-component-end-line="122" data-component-file="TrustBadgeCarousel.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-shrink-0%20w-64%20snap-start%20bg-card%20rounded-lg%20p-4%20shadow-card%20hover%3Ashadow-elevated%20transition-smooth%22%7D"
            key={index}
            className="flex-shrink-0 w-64 snap-start bg-card rounded-lg p-4 shadow-card hover:shadow-elevated transition-smooth"
          >
            <div data-component-id="src/components/common/TrustBadgeCarousel.tsx:109:12" data-component-path="src/components/common/TrustBadgeCarousel.tsx" data-component-line="109" data-component-end-line="121" data-component-file="TrustBadgeCarousel.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-start%20space-x-3%22%7D" className="flex items-start space-x-3">
              <div data-component-id="src/components/common/TrustBadgeCarousel.tsx:110:14" data-component-path="src/components/common/TrustBadgeCarousel.tsx" data-component-line="110" data-component-end-line="112" data-component-file="TrustBadgeCarousel.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-shrink-0%20w-12%20h-12%20bg-primary%2F10%20rounded-lg%20flex%20items-center%20justify-center%22%7D" className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon data-component-id="src/components/common/TrustBadgeCarousel.tsx:111:16" data-component-path="src/components/common/TrustBadgeCarousel.tsx" data-component-line="111" data-component-end-line="111" data-component-file="TrustBadgeCarousel.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22className%22%3A%22text-primary%22%7D" name={badge.icon as any} size={24} variant="solid" className="text-primary" />
              </div>
              <div data-component-id="src/components/common/TrustBadgeCarousel.tsx:113:14" data-component-path="src/components/common/TrustBadgeCarousel.tsx" data-component-line="113" data-component-end-line="120" data-component-file="TrustBadgeCarousel.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%22%7D" className="flex-1">
                <h3 data-component-id="src/components/common/TrustBadgeCarousel.tsx:114:16" data-component-path="src/components/common/TrustBadgeCarousel.tsx" data-component-line="114" data-component-end-line="116" data-component-file="TrustBadgeCarousel.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-sm%20font-headline%20font-semibold%20text-foreground%20mb-1%22%7D" className="text-sm font-headline font-semibold text-foreground mb-1">
                  {badge.title}
                </h3>
                <p data-component-id="src/components/common/TrustBadgeCarousel.tsx:117:16" data-component-path="src/components/common/TrustBadgeCarousel.tsx" data-component-line="117" data-component-end-line="119" data-component-file="TrustBadgeCarousel.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xs%20text-muted-foreground%20leading-relaxed%22%7D" className="text-xs text-muted-foreground leading-relaxed">
                  {badge.description}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Scroll Right Button */}
      {canScrollRight && (
        <button data-component-id="src/components/common/TrustBadgeCarousel.tsx:128:8" data-component-path="src/components/common/TrustBadgeCarousel.tsx" data-component-line="128" data-component-end-line="134" data-component-file="TrustBadgeCarousel.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22absolute%20right-0%20top-1%2F2%20-translate-y-1%2F2%20z-10%20bg-card%20shadow-elevated%20rounded-full%20p-2%20hover%3Abg-muted%20transition-smooth%22%7D"
          onClick={() => scroll('right')}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-card shadow-elevated rounded-full p-2 hover:bg-muted transition-smooth"
          aria-label="Scroll right"
        >
          <Icon data-component-id="src/components/common/TrustBadgeCarousel.tsx:133:10" data-component-path="src/components/common/TrustBadgeCarousel.tsx" data-component-line="133" data-component-end-line="133" data-component-file="TrustBadgeCarousel.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ChevronRightIcon%22%7D" name="ChevronRightIcon" size={20} variant="solid" />
        </button>
      )}
    </div>
  );
};

export default TrustBadgeCarousel;